import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Clock, X, Loader } from 'lucide-react';
import { getAddressHistory, removeAddressFromHistory } from '../utils/addressHistoryUtils';

const GEOAPIFY_API_KEY = '1618c6fc10694ceba50c4d304c5b0073';

const AddressAutocomplete = ({ value, onChange, onSubmit, onSelectParsed, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addressHistory, setAddressHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setAddressHistory(getAddressHistory());
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      // If user hasn't typed a US state abbreviation or name, append TX to bias toward Texas
      const hasState = /\b([A-Z]{2})\b/.test(text) || /texas|california|florida|new york|ohio|indiana|illinois/i.test(text);
      const searchText = hasState ? text : `${text} TX`;
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(searchText)}&filter=countrycode:us&format=json&apiKey=${GEOAPIFY_API_KEY}`;
      const res = await fetch(url);
      const data = await res.json();
      const results = (data.results || []).slice(0, 6).map((r) => {
        const clean = (r.formatted || '').replace(/,?\s*United States of America$/i, '').replace(/,?\s*United States$/i, '').trim();
        return {
          label: clean,
          street: r.address_line1 || '',
          city: r.city || r.town || r.village || '',
          state: r.state_code || r.state || '',
          zip: r.postcode || '',
          full: clean,
        };
      });
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setShowHistory(false);
    } catch (err) {
      console.error('Geoapify error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    setShowHistory(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleFocus = () => {
    if (!value || value.length < 3) {
      const history = getAddressHistory();
      setAddressHistory(history);
      if (history.length > 0) {
        setShowHistory(true);
        setShowDropdown(false);
      }
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion.full);
    if (onSelectParsed) onSelectParsed(suggestion); // pass pre-parsed components to parent
    setSuggestions([]);
    setShowDropdown(false);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  const handleSelectHistory = (address) => {
    onChange(address);
    setShowHistory(false);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleRemoveHistory = (e, address) => {
    e.stopPropagation();
    removeAddressFromHistory(address);
    setAddressHistory(getAddressHistory());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setShowDropdown(false);
      setShowHistory(false);
      if (onSubmit) onSubmit();
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setShowHistory(false);
    }
  };

  const isOpen = (showDropdown && suggestions.length > 0) || (showHistory && addressHistory.length > 0);

  return (
    <div className="flex-1 relative">
      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
      {loading && (
        <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin z-10" />
      )}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Enter property address..."
        className="w-full pl-10 pr-8 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full bottom-full mb-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden"
          style={{ maxHeight: '220px' }}
        >
          <div className="overflow-y-auto" style={{ maxHeight: '220px' }}>
            {showHistory && addressHistory.length > 0 && (
              <>
                <div className="px-3 py-2 border-b border-gray-700 sticky top-0 bg-gray-800">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    <span>Recent Searches</span>
                  </div>
                </div>
                {addressHistory.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-700 cursor-pointer group"
                    style={{ minHeight: '44px' }}
                    onClick={() => handleSelectHistory(item.address)}
                  >
                    <div className="flex items-start gap-2 flex-1">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white leading-snug">{item.address}</span>
                    </div>
                    <button
                      onClick={(e) => handleRemoveHistory(e, item.address)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-600 rounded transition-opacity flex-shrink-0 ml-2"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
              </>
            )}

            {showDropdown && suggestions.length > 0 && (
              <>
                <div className="px-3 py-2 border-b border-gray-700 sticky top-0 bg-gray-800">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <MapPin className="w-3 h-3" />
                    <span>Address Suggestions</span>
                  </div>
                </div>
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer"
                    style={{ minHeight: '44px' }}
                    onClick={() => handleSelectSuggestion(s)}
                  >
                    <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-white leading-snug">{s.label}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
